import { FC, useEffect, useContext } from "react";
import { captureErrorInfo } from "@src/taskpane/helpers/errorHandler";
import { getCurrentWorkspace } from "@src/lib/walter/authContext";
import { migrate } from "@src/lib/migrations/migrate";
import { WaitUntilCustomXmlPartsAreReady } from "@src/lib/sync-engine/operations/WaitUntilCustomXmlPartsAreReady";
import { syncEngine } from "@src/lib/sync-engine/SyncEngine";
import type { WalterUser } from "@src/lib/auth/OAuthService";
import { WalterApi } from "@src/lib/walter/api";
import { WalterContext } from "@src/taskpane/providers/walter";
import { useAppReady, appReady } from "@src/taskpane/hooks/useAppReady";
import { useOfficeInitialized } from "@src/taskpane/hooks/useOfficeInitialized";
import { useAuthState } from "@src/taskpane/hooks/useAuthState";
import useToasts from "@src/taskpane/hooks/useToasts";

const setError = (header = "Something went wrong", innerText = "Please reload the page or contact support.") => {
  const loadHeader = document.getElementById("load-screen-header");
  const loadText = document.getElementById("load-screen-text");

  if (loadHeader) loadHeader.innerText = header;
  if (loadText) loadText.innerText = innerText;
};

interface AppSetupProps {
  setCurrentUser: (user: WalterUser) => void;
  switchWorkspace: (workspace: any) => void;
}

/**
 * Component that handles app setup logic inside the WalterProvider context.
 * This allows it to access the shared state and update it properly.
 */
export const AppSetup: FC<AppSetupProps> = ({ setCurrentUser, switchWorkspace }) => {
  const { showErrors } = useToasts();
  const officeReady = useOfficeInitialized();
  const { isAppReady } = useAppReady();
  const { isLoggedIn, setIsLoggedIn } = useContext(WalterContext);

  // Handle authentication state checking
  useAuthState(setIsLoggedIn);

  // Handle app data loading (triggered after Office is ready and user is logged in)
  useEffect(() => {
    if (isAppReady || !isLoggedIn) {
      return;
    }

    let whoamiReqAbortController: AbortController;

    async function setupApp() {
      try {
        let whoamiResponse: Promise<WalterUser> | null;
        ({ abortController: whoamiReqAbortController, response: whoamiResponse } =
          WalterApi.get<WalterUser>("api/users/whoami"));

        if (whoamiResponse) {
          const user = await whoamiResponse;
          const currentWorkspace = getCurrentWorkspace(user, () => {
            showErrors({
              title: "No workspaces found",
              messages: ["You must have a workspace in walter with accessible legal entities to use this add-in."],
            });
          });
          switchWorkspace(currentWorkspace);
          setCurrentUser(user);
          await onDataLoaded();

          appReady();
        } else {
          captureErrorInfo(new Error("Failed to set current user info"));
          showErrors({ title: "Error initializing user" });
        }
      } catch (error) {
        captureErrorInfo(error);
        showErrors({ title: "Error initializing user" });
      }
    }

    async function onDataLoaded() {
      try {
        await syncEngine.run(async () => {
          await syncEngine.perform(new WaitUntilCustomXmlPartsAreReady());
          await migrate("local");
        });
      } catch (error) {
        captureErrorInfo(error);
        setError();
      }
    }

    void setupApp();

    return () => {
      if (whoamiReqAbortController) whoamiReqAbortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- these are the only dependencies that we care about - adding the other ones would cause re-runs
  }, [officeReady, isLoggedIn, isAppReady]);

  return null;
};
